import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  MessageCircle, Check, X, Trash2, Edit, Eye, EyeOff,
  Star, MapPin, Calendar, Search, Filter, Loader2, Plus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tripTypes = [
  { value: "trek", label: "Trek dans le désert" },
  { value: "4x4", label: "Circuit 4x4" },
  { value: "stage", label: "Stage ou retraite" },
  { value: "bivouac", label: "Nuit en bivouac" },
  { value: "famille", label: "Voyage en famille" },
  { value: "autre", label: "Autre" },
];

const AdminExperiences = () => {
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, pending, approved
  const [search, setSearch] = useState("");
  const [editingExperience, setEditingExperience] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newExperience, setNewExperience] = useState({
    author_name: "",
    author_email: "",
    author_location: "",
    trip_type: "",
    trip_date: "",
    content: "",
    rating: 5,
  });

  const token = localStorage.getItem("admin_token");

  const fetchExperiences = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/experiences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExperiences(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des témoignages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExperiences();
  }, []);

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API}/admin/experiences/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Témoignage approuvé et publié");
      fetchExperiences();
    } catch (error) {
      toast.error("Erreur lors de l'approbation");
    }
  };

  const handleReject = async (id) => {
    try {
      await axios.put(`${API}/admin/experiences/${id}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Témoignage rejeté");
      fetchExperiences();
    } catch (error) {
      toast.error("Erreur lors du rejet");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/experiences/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Témoignage supprimé");
      setDeleteId(null);
      fetchExperiences();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/experiences/${editingExperience.id}`, editingExperience, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Témoignage mis à jour");
      setEditingExperience(null);
      fetchExperiences();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newExperience.author_name || !newExperience.content) {
      toast.error("Le nom et le contenu sont requis");
      return;
    }
    try {
      await axios.post(`${API}/admin/experiences`, newExperience, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Témoignage créé et publié");
      setShowAddDialog(false);
      setNewExperience({
        author_name: "",
        author_email: "",
        author_location: "",
        trip_type: "",
        trip_date: "",
        content: "",
        rating: 5,
      });
      fetchExperiences();
    } catch (error) {
      toast.error("Erreur lors de la création");
    }
  };

  const toggleVisibility = async (exp) => {
    try {
      await axios.put(`${API}/admin/experiences/${exp.id}`, {
        is_visible: !exp.is_visible
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(exp.is_visible ? "Témoignage masqué" : "Témoignage affiché");
      fetchExperiences();
    } catch (error) {
      toast.error("Erreur lors de la modification");
    }
  };

  // Filter experiences
  const filteredExperiences = experiences.filter(exp => {
    const matchesFilter = 
      filter === "all" ? true :
      filter === "pending" ? !exp.is_approved :
      filter === "approved" ? exp.is_approved : true;

    const matchesSearch = search === "" || 
      exp.author_name.toLowerCase().includes(search.toLowerCase()) ||
      exp.content.toLowerCase().includes(search.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  const pendingCount = experiences.filter(e => !e.is_approved).length;

  return (
    <div className="p-6" data-testid="admin-experiences">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageCircle className="h-6 w-6 text-primary" />
            Retours d'Expériences
          </h1>
          <p className="text-muted-foreground mt-1">
            {experiences.length} témoignages au total
            {pendingCount > 0 && (
              <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-700">
                {pendingCount} en attente
              </Badge>
            )}
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-experience-btn">
          <Plus className="h-4 w-4 mr-2" />
          Ajouter un témoignage
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par nom ou contenu..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={filter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("all")}
          >
            Tous
          </Button>
          <Button
            variant={filter === "pending" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("pending")}
          >
            En attente ({pendingCount})
          </Button>
          <Button
            variant={filter === "approved" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("approved")}
          >
            Approuvés
          </Button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : filteredExperiences.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">
          Aucun témoignage trouvé
        </div>
      ) : (
        <div className="space-y-4">
          {filteredExperiences.map((exp) => (
            <div 
              key={exp.id}
              className={`bg-white rounded-xl border p-6 ${!exp.is_approved ? "border-amber-200 bg-amber-50/30" : ""}`}
              data-testid={`experience-item-${exp.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  {/* Status badges */}
                  <div className="flex items-center gap-2 mb-3">
                    {!exp.is_approved && (
                      <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                        En attente
                      </Badge>
                    )}
                    {exp.is_approved && exp.is_visible && (
                      <Badge variant="secondary" className="bg-green-100 text-green-700">
                        Publié
                      </Badge>
                    )}
                    {exp.is_approved && !exp.is_visible && (
                      <Badge variant="secondary" className="bg-gray-100 text-gray-700">
                        Masqué
                      </Badge>
                    )}
                    {exp.trip_type && (
                      <Badge variant="outline">
                        {tripTypes.find(t => t.value === exp.trip_type)?.label || exp.trip_type}
                      </Badge>
                    )}
                  </div>

                  {/* Author info */}
                  <div className="flex items-center gap-4 mb-3">
                    <span className="font-semibold">{exp.author_name}</span>
                    {exp.author_location && (
                      <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {exp.author_location}
                      </span>
                    )}
                    {exp.trip_date && (
                      <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {exp.trip_date}
                      </span>
                    )}
                    {exp.rating && (
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`h-3 w-3 ${i < exp.rating ? "text-amber-400 fill-amber-400" : "text-gray-200"}`} 
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Content preview */}
                  <p className="text-sm text-muted-foreground line-clamp-3 whitespace-pre-line">
                    {exp.content}
                  </p>

                  {/* Email if present */}
                  {exp.author_email && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Email: {exp.author_email}
                    </p>
                  )}

                  {/* Date */}
                  <p className="text-xs text-muted-foreground mt-2">
                    Soumis le {new Date(exp.created_at).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit"
                    })}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2">
                  {!exp.is_approved && (
                    <>
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleApprove(exp.id)}
                        data-testid={`approve-${exp.id}`}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Approuver
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleReject(exp.id)}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Rejeter
                      </Button>
                    </>
                  )}
                  
                  {exp.is_approved && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => toggleVisibility(exp)}
                    >
                      {exp.is_visible ? (
                        <>
                          <EyeOff className="h-4 w-4 mr-1" />
                          Masquer
                        </>
                      ) : (
                        <>
                          <Eye className="h-4 w-4 mr-1" />
                          Afficher
                        </>
                      )}
                    </Button>
                  )}

                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setEditingExperience({...exp})}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Modifier
                  </Button>

                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => setDeleteId(exp.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Supprimer
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingExperience} onOpenChange={() => setEditingExperience(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le témoignage</DialogTitle>
            <DialogDescription>
              Modifiez les informations du témoignage
            </DialogDescription>
          </DialogHeader>
          
          {editingExperience && (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom</Label>
                  <Input
                    value={editingExperience.author_name}
                    onChange={(e) => setEditingExperience(prev => ({...prev, author_name: e.target.value}))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Lieu</Label>
                  <Input
                    value={editingExperience.author_location || ""}
                    onChange={(e) => setEditingExperience(prev => ({...prev, author_location: e.target.value}))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type de voyage</Label>
                  <Select 
                    value={editingExperience.trip_type || ""} 
                    onValueChange={(value) => setEditingExperience(prev => ({...prev, trip_type: value}))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez..." />
                    </SelectTrigger>
                    <SelectContent>
                      {tripTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Période</Label>
                  <Input
                    value={editingExperience.trip_date || ""}
                    onChange={(e) => setEditingExperience(prev => ({...prev, trip_date: e.target.value}))}
                    placeholder="Février 2025"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Note</Label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setEditingExperience(prev => ({...prev, rating: star}))}
                      className="p-1"
                    >
                      <Star 
                        className={`h-6 w-6 ${
                          star <= editingExperience.rating ? "text-amber-400 fill-amber-400" : "text-gray-300"
                        }`} 
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Témoignage</Label>
                <Textarea
                  value={editingExperience.content}
                  onChange={(e) => setEditingExperience(prev => ({...prev, content: e.target.value}))}
                  rows={8}
                  required
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingExperience.is_approved}
                    onChange={(e) => setEditingExperience(prev => ({...prev, is_approved: e.target.checked}))}
                    className="rounded"
                  />
                  Approuvé
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingExperience.is_visible}
                    onChange={(e) => setEditingExperience(prev => ({...prev, is_visible: e.target.checked}))}
                    className="rounded"
                  />
                  Visible
                </label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditingExperience(null)}>
                  Annuler
                </Button>
                <Button type="submit">Enregistrer</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ajouter un témoignage</DialogTitle>
            <DialogDescription>
              Créer un nouveau témoignage (sera automatiquement approuvé et publié)
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input
                  value={newExperience.author_name}
                  onChange={(e) => setNewExperience(prev => ({...prev, author_name: e.target.value}))}
                  placeholder="Jean Dupont"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Lieu</Label>
                <Input
                  value={newExperience.author_location}
                  onChange={(e) => setNewExperience(prev => ({...prev, author_location: e.target.value}))}
                  placeholder="Paris, France"
                />
              </div>
              <div className="space-y-2">
                <Label>Type de voyage</Label>
                <Select 
                  value={newExperience.trip_type} 
                  onValueChange={(value) => setNewExperience(prev => ({...prev, trip_type: value}))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tripTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Période</Label>
                <Input
                  value={newExperience.trip_date}
                  onChange={(e) => setNewExperience(prev => ({...prev, trip_date: e.target.value}))}
                  placeholder="Février 2025"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setNewExperience(prev => ({...prev, rating: star}))}
                    className="p-1"
                  >
                    <Star 
                      className={`h-6 w-6 ${
                        star <= newExperience.rating ? "text-amber-400 fill-amber-400" : "text-gray-300"
                      }`} 
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Témoignage *</Label>
              <Textarea
                value={newExperience.content}
                onChange={(e) => setNewExperience(prev => ({...prev, content: e.target.value}))}
                rows={6}
                placeholder="Écrivez le témoignage ici..."
                required
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Annuler
              </Button>
              <Button type="submit">Créer et publier</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce témoignage ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le témoignage sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700"
              onClick={() => handleDelete(deleteId)}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminExperiences;
